import React from 'react';
import { BaseStep, FormField, useFormData, BaseStepProps } from '../BaseStep';
import FormSection from '../../../FormSection';
import { Toggle } from '../Toggle';
import supportIcon from '../../../../assets/icons/support.png';
import expertiseIcon from '../../../../assets/icons/expertise.png';
import trophyIcon from '../../../../assets/trophy.png';
import { useProfile } from '../../../../contexts/ProfileContext';
import { updateFrequencySelectOptions } from '../../../../constants/support';

const ExternalSupportStep: React.FC<BaseStepProps> = ({
  userType,
  onCreateProfile,
  hasProfile,
  onComplete,
  shouldValidate,
  onValidationHandled,
  onValidationResult,
}) => {
  const { investorProfile } = useProfile();

  // Helper to parse expertise array
  const parseExpertise = (storedData: string | string[] | undefined): string[] => {
    if (Array.isArray(storedData)) return storedData;
    if (typeof storedData === 'string' && storedData) {
      try {
        const parsed = JSON.parse(storedData);
        if (Array.isArray(parsed)) return parsed;
      } catch {
        // If parsing fails, return empty array
      }
    }
    return [];
  };

  const { formData, handleInputChange, setFormData } = useFormData({
    preferStartupProgram:
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      String((investorProfile as any)?.preferStartupProgram || ''),
    otherExpertise:
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      String((investorProfile as any)?.otherExpertise || ''),
    updateFrequency:
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      String((investorProfile as any)?.updateFrequency || 'annually'),
  });

  // Initialize expertise toggles from profile
  const expertiseArray = parseExpertise(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (investorProfile as any)?.expertise
  );
  const [industriesExpertise, setIndustriesExpertise] = React.useState<boolean>(
    expertiseArray.includes('industries-expertise')
  );
  const [industriesContacts, setIndustriesContacts] = React.useState<boolean>(
    expertiseArray.includes('industries-contacts')
  );
  const [otherExpertise, setOtherExpertise] = React.useState<boolean>(
    expertiseArray.includes('other')
  );

  // Update formData with expertise array
  React.useEffect(() => {
    const expertise: string[] = [];
    if (industriesExpertise) expertise.push('industries-expertise');
    if (industriesContacts) expertise.push('industries-contacts');
    if (otherExpertise) expertise.push('other');

    setFormData(prev => ({
      ...prev,
      expertise: expertise,
    }));
  }, [industriesExpertise, industriesContacts, otherExpertise, setFormData]);

  // Listen for validation requests from parent
  // Nothing is required in this step, so validation always passes
  React.useEffect(() => {
    if (shouldValidate) {
      // Always valid - nothing is required
      const isValid = true;

      // Notify parent about validation result
      if (onValidationResult) {
        onValidationResult(isValid);
      }

      // Notify parent that validation was handled
      if (onValidationHandled) {
        onValidationHandled();
      }
    }
  }, [shouldValidate, onValidationHandled, onValidationResult]);

  // Expose form data to parent component
  React.useEffect(() => {
    if (onCreateProfile) {
      onCreateProfile(formData);
    }
  }, [formData, onCreateProfile]);

  return (
    <BaseStep
      userType={userType}
      onCreateProfile={onCreateProfile}
      hasProfile={hasProfile}
      onComplete={onComplete}
    >
      <FormSection icon={supportIcon} title="SUPPORT">
        <div>
          <label className="block text-sm sm:text-base font-medium text-white mb-3 text-left">
            Prefer Startup participation in a generator, incubator or accelerator program
          </label>
          <div className="flex space-x-6">
            <label className="flex items-center cursor-pointer">
              <input
                type="radio"
                name="preferStartupProgram"
                value="yes"
                checked={formData.preferStartupProgram === 'yes'}
                onChange={handleInputChange}
                className="w-4 h-4 text-emerald-500 bg-emerald-500 border-emerald-500 focus:ring-emerald-500 checked:bg-emerald-500"
                style={{ accentColor: '#92d050' }}
              />
              <span className="ml-2 text-white">Yes</span>
            </label>
            <label className="flex items-center cursor-pointer">
              <input
                type="radio"
                name="preferStartupProgram"
                value="no"
                checked={formData.preferStartupProgram === 'no'}
                onChange={handleInputChange}
                className="w-4 h-4 text-emerald-500 bg-emerald-500 border-emerald-500 focus:ring-emerald-500 checked:bg-emerald-500"
                style={{ accentColor: '#92d050' }}
              />
              <span className="ml-2 text-white">No</span>
            </label>
            <label className="flex items-center cursor-pointer">
              <input
                type="radio"
                name="preferStartupProgram"
                value="doesnt-matter"
                checked={formData.preferStartupProgram === 'doesnt-matter'}
                onChange={handleInputChange}
                className="w-4 h-4 text-emerald-500 bg-emerald-500 border-emerald-500 focus:ring-emerald-500 checked:bg-emerald-500"
                style={{ accentColor: '#92d050' }}
              />
              <span className="ml-2 text-white">Doesn&apos;t matter</span>
            </label>
          </div>
        </div>
      </FormSection>

      <FormSection icon={expertiseIcon} title="EXPERTISE">
        <div>
          <label className="block text-sm sm:text-base font-medium text-white mb-4 text-left">
            Additional value/expertise you contribute to the startups (How &apos;smart&apos; is your
            money?)
          </label>
          <div className="space-y-3">
            <Toggle
              name="industries-expertise"
              label="I/we offer expertise in the fields selected in the 'Industries' section"
              value={industriesExpertise}
              onChange={setIndustriesExpertise}
            />
            <Toggle
              name="industries-contacts"
              label="I offer contacts in the fields selected in the 'Industries' section"
              value={industriesContacts}
              onChange={setIndustriesContacts}
            />
            <Toggle
              name="other"
              label="Other"
              value={otherExpertise}
              onChange={setOtherExpertise}
            />
          </div>
        </div>

        {otherExpertise && (
          <FormField
            name="otherExpertise"
            label="What expertise?"
            value={String(formData.otherExpertise || '')}
            onChange={handleInputChange}
            placeholder="Specify the expertise"
            required={false}
            error={undefined}
          />
        )}
      </FormSection>

      <FormSection icon={trophyIcon} title="PROGRESS">
        <div>
          <label
            htmlFor="updateFrequency"
            className="block text-sm sm:text-base font-medium text-white mb-2 text-left"
          >
            Remind me to review / update my match criteria in:
          </label>
          <select
            id="updateFrequency"
            name="updateFrequency"
            value={String(formData.updateFrequency || '')}
            onChange={handleInputChange}
            className="w-full px-2 py-1.5 sm:px-3 sm:py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500 bg-white text-gray-900"
          >
            {updateFrequencySelectOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.name}
              </option>
            ))}
          </select>
        </div>
      </FormSection>
    </BaseStep>
  );
};

export default ExternalSupportStep;
